import { useRef, useState } from "react";
import { Upload, X } from "lucide-react";
import { fileToCompressedDataUri } from "@/lib/imageUtil";

/**
 * HeroPhotoPicker — drag & drop / click-to-browse hero photo selector.
 * Compresses to a ≤1600px JPEG data URI stored in vessel.hero_photo_url.
 * Still accepts a pasted image URL as a fallback for stock photos.
 */
export const HeroPhotoPicker = ({ value, onChange, disabled }) => {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const [err, setErr] = useState("");

    const handleFile = async (file) => {
        if (!file || disabled) return;
        if (!file.type.startsWith("image/")) {
            setErr("Please choose an image file (JPG, PNG, WebP…).");
            return;
        }
        setErr("");
        try {
            const dataUri = await fileToCompressedDataUri(file, { maxDim: 1600, quality: 0.8 });
            onChange(dataUri);
        } catch {
            setErr("Couldn't read that image — try another file.");
        }
    };

    if (value) {
        return (
            <div data-testid="hero-photo-preview" style={{ position: "relative", borderRadius: 12, overflow: "hidden", border: "1px solid var(--bm-border)" }}>
                <img src={value} alt="Vessel hero" style={{ display: "block", width: "100%", height: 160, objectFit: "cover" }} />
                {!disabled && (
                    <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 6 }}>
                        <button
                            type="button"
                            data-testid="hero-photo-replace-btn"
                            className="bm-btn bm-btn-ghost"
                            style={{ padding: "6px 12px", fontSize: 12, background: "rgba(5,15,28,0.75)", backdropFilter: "blur(4px)" }}
                            onClick={() => inputRef.current?.click()}
                        >
                            <Upload size={13} /> Replace
                        </button>
                        <button
                            type="button"
                            data-testid="hero-photo-remove-btn"
                            className="bm-btn bm-btn-ghost"
                            style={{ padding: "6px 10px", fontSize: 12, background: "rgba(5,15,28,0.75)", backdropFilter: "blur(4px)" }}
                            onClick={() => onChange("")}
                            title="Remove photo"
                        >
                            <X size={13} />
                        </button>
                    </div>
                )}
                <input ref={inputRef} type="file" accept="image/*" hidden data-testid="hero-photo-file-input" onChange={(e) => handleFile(e.target.files?.[0])} />
            </div>
        );
    }

    return (
        <div>
            <div
                data-testid="hero-photo-dropzone"
                onClick={() => !disabled && inputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => { e.preventDefault(); setDragOver(false); handleFile(e.dataTransfer.files?.[0]); }}
                style={{
                    border: `1.5px dashed ${dragOver ? "var(--bm-gold)" : "var(--bm-border)"}`,
                    background: dragOver ? "rgba(212,181,114,0.08)" : "rgba(255,255,255,0.02)",
                    borderRadius: 12,
                    padding: "26px 18px",
                    textAlign: "center",
                    cursor: disabled ? "not-allowed" : "pointer",
                    transition: "border-color 0.15s, background-color 0.15s",
                    opacity: disabled ? 0.5 : 1,
                }}
            >
                <Upload size={20} color="var(--bm-gold)" style={{ marginBottom: 8 }} />
                <div style={{ fontSize: 13, color: "var(--bm-text)" }}>
                    Drag & drop a photo, or <span style={{ color: "var(--bm-gold)" }}>browse</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--bm-text-muted)", marginTop: 4 }}>
                    JPG, PNG or WebP — we'll resize it for you
                </div>
            </div>
            <input ref={inputRef} type="file" accept="image/*" hidden data-testid="hero-photo-file-input" onChange={(e) => handleFile(e.target.files?.[0])} />
            {!disabled && (
                <input
                    data-testid="hero-photo-url-input"
                    className="bm-input"
                    style={{ marginTop: 8, fontSize: 12 }}
                    placeholder="…or paste an image URL (https://...)"
                    onBlur={(e) => { const v = e.target.value.trim(); if (v) onChange(v); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); const v = e.target.value.trim(); if (v) onChange(v); } }}
                />
            )}
            {err && <div data-testid="hero-photo-error" style={{ marginTop: 6, fontSize: 12, color: "var(--bm-bad)" }}>{err}</div>}
        </div>
    );
};
